import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  adminService,
  type HomeBannerRow,
} from "@/project/jain_elibrary-admin/services/adminService";
import { Loader2, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { showErrorToast } from "@/utils/errorHandler";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const emptyForm = (): Omit<
  HomeBannerRow,
  "id" | "created_on" | "updated_on"
> => ({
  sort_order: 0,
  image_url: "",
  description: "",
  link_url: "",
  is_active: true,
});

const Banner = () => {
  const [rows, setRows] = useState<HomeBannerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<HomeBannerRow | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const list = await adminService.getHomeBanners();
      setRows(list);
    } catch (e: unknown) {
      console.error(e);
      showErrorToast(e, "Could not load banners.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (b: HomeBannerRow) => {
    setEditing(b);
    setForm({
      sort_order: b.sort_order,
      image_url: b.image_url,
      description: b.description,
      link_url: b.link_url,
      is_active: b.is_active,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.image_url.trim()) {
      toast.error("Image URL is required.");
      return;
    }
    try {
      setSaving(true);
      if (editing) {
        await adminService.patchHomeBanner(editing.id, form);
        toast.success("Banner updated.");
      } else {
        await adminService.createHomeBanner(form);
        toast.success("Banner created.");
      }
      setDialogOpen(false);
      await load();
    } catch (e: unknown) {
      showErrorToast(e, "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b: HomeBannerRow) => {
    if (!window.confirm("Delete this banner?")) {
      return;
    }
    try {
      await adminService.deleteHomeBanner(b.id);
      toast.success("Deleted.");
      await load();
    } catch (e: unknown) {
      showErrorToast(e, "Delete failed.");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">
              Home page banners
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Images appear in a strip on the public home page (below the hero).
              Use a direct image URL (CDN or uploaded file URL).
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => void load()}
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={openCreate}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add banner
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No banners yet. Add one to show on the public site.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {rows.map(b => (
              <div
                key={b.id}
                className="overflow-hidden rounded-lg border border-border bg-card"
              >
                {b.image_url ? (
                  <img
                    src={b.image_url}
                    alt={b.description || "Banner"}
                    className="h-48 w-full object-cover"
                    onError={e => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <div className="flex h-48 items-center justify-center bg-muted text-sm text-muted-foreground">
                    No image URL
                  </div>
                )}
                <div className="space-y-2 p-4">
                  <p className="text-sm text-muted-foreground">
                    {b.description || "—"}
                  </p>
                  <p className="break-all text-xs font-medium">
                    Link: {b.link_url || "—"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Order: {b.sort_order} · {b.is_active ? "Active" : "Hidden"}
                  </p>
                  <div className="flex gap-2 pt-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => openEdit(b)}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      onClick={() => void remove(b)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit banner" : "Add banner"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label required>Image URL</Label>
              <Input
                placeholder="https://..."
                value={form.image_url}
                onChange={e =>
                  setForm(f => ({ ...f, image_url: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label required>Description (alt text)</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={e =>
                  setForm(f => ({ ...f, description: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Link URL (optional)</Label>
              <Input
                placeholder="/about or https://..."
                value={form.link_url}
                onChange={e =>
                  setForm(f => ({ ...f, link_url: e.target.value }))
                }
              />
            </div>
            <div className="flex gap-4">
              <div className="space-y-2">
                <Label>Sort order</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.sort_order}
                  onChange={e =>
                    setForm(f => ({
                      ...f,
                      sort_order: Math.max(0, Number(e.target.value) || 0),
                    }))
                  }
                />
              </div>
              <div className="flex items-end gap-2 pb-2">
                <Switch
                  id="banner-active"
                  checked={form.is_active}
                  onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))}
                />
                <Label htmlFor="banner-active">Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={saving} onClick={() => void save()}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default Banner;
